<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="action-bar">
        <div class="filter-group">
          <input 
            type="text" 
            class="form-control search-input" 
            placeholder="Search by Name..." 
            v-model="searchQuery" 
          />
          <select 
            class="form-control search-input" 
            v-model="typeQuery"
            style="text-transform: uppercase;"
          >
            <option value="">All Types</option>
            <option v-for="type in uniqueStaffTypes" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
        <button type="button" class="btn button-default new-item-btn" @click="newStaff">
          Add New Staff
        </button>
      </div>

      <!-- added by isindu | 2025/ 12/ 02 -->
      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <!-- added v-else by isindu | 2025/ 12/ 02 -->
      <table v-else class="table">
        <thead>
          <tr class="border-bottom-primary">
            <th scope="col">Name and Mobile</th>
            <th scope="col">Type</th>
            <th scope="col">Status</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="staff in filteredStaff" :key="staff.id">
            <td>
              <div>{{ staff.name }}</div>
              <div class="mobile-number" v-if="isOfficeStaff(staff) && staff.mobile">
                ({{ staff.mobile }})
              </div>
            </td>
            <td style="text-transform: uppercase;">{{ staff.staff_type || staff.staf_type }}</td>
            <td>
              <span :class="staff.is_active === '1' ? 'badge badge-active' : 'badge badge-cancel'">
                {{ staff.is_active === '1' ? 'ACTIVE' : 'INACTIVE' }}
              </span>
            </td>
            <td>
              <i class="fa fa-edit" @click="editStaff(staff)"></i>
              <i class="fa fa-eye action-icon" aria-hidden="true" @click="viewStaff(staff)"></i>
              <i
                  v-if="canUnblock(staff)"
                  class="fa fa-unlock unblock-icon"
                  title="Unblock User"
                  @click="confirmUnblock(staff.id)"
              ></i>
            </td>
          </tr>

          <tr v-if="!filteredStaff.length">
            <td colspan="4" class="text-center no-data">No staff found.</td>
          </tr>
        </tbody>
      </table>

      <!-- Use the separate modal component -->
      <ViewDistributorStaff :show="showStaffModal" :staff-id="selectedStaffId" :staff-type="selectedStaffType" @close="closeStaffModal" />

      <!-- Unblock Confirmation Dialog -->
      <div v-if="showUnblockDialog" class="dialog-overlay">
        <div class="dialog-box">
          <div class="dialog-header">
            <h3>Confirm user unblock</h3>
          </div>
          <div class="dialog-body">
            <p>This user account was blocked. Are you want to unblock this user now?</p>
          </div>
          <div class="dialog-footer">
            <button class="btn btn-danger" @click="cancelUnblock">No</button>
            <button class="btn btn-success" @click="processUnblock">
              <span v-if="unblocking" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-if="!unblocking">Yes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { fetchAllStaffList, unblockUser } from "@/services/dataService";
import ViewDistributorStaff from "@/pages/Modal/ViewDistributorStaff.vue"; // Adjust path as needed
import { useToast } from "vue-toastification";

export default {
  name: 'DistributorStaff',
  components: {
    ViewDistributorStaff
  },
  data() {
    return {
      token: localStorage.getItem('token_set'),
      companyId: localStorage.getItem('company_id'),
      profileId: localStorage.getItem('profile_id'),
      reportingProfileId: localStorage.getItem('reportingL1ProfileId'),
      userCategory: localStorage.getItem('user_label'),
      router: useRouter(),
      loading: false,
      staffList: [],
      searchQuery: "",
      typeQuery: "",
      showStaffModal: false,
      selectedStaffId: null,
      selectedStaffType: null,
      showUnblockDialog: false,
      unblockUserId: null,
      unblocking: false,
    }
  },

  computed: {
    uniqueStaffTypes() {
      const types = this.staffList.map(staff => staff.staff_type || staff.staf_type).filter(Boolean);
      return [...new Set(types)];
    },
    filteredStaff() {
      return this.staffList.filter(staff => {
        const matchesName = !this.searchQuery || (staff.name || "").toLowerCase().includes(this.searchQuery.toLowerCase());
        const staffType = staff.staff_type || staff.staf_type || "";
        const matchesType = !this.typeQuery || staffType === this.typeQuery;
        return matchesName && matchesType;
      });
    }
  },

  created() {
    this.fetchStaff();
  },

  watch: {
    showStaffModal(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'auto';
      }
    }
  },

  methods: {
    async fetchStaff() {
      this.loading = true;
      try {
        let idToUse = this.profileId;
        if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
          idToUse = this.reportingProfileId;
        }
        const response = await fetchAllStaffList(idToUse, this.token);
        const data = response.data.data || [];
        this.staffList = Array.isArray(data) ? data : Object.values(data).flat();
      } catch (error) {
        console.error("Error fetching staff list:", error);
        this.staffList = [];
      } finally {
        this.loading = false;
      }
    },

    viewStaff(staff) {
      let mappedType = '';
      const typeStr = String(staff.staff_type || staff.staf_type || '').toUpperCase().trim();
      if (typeStr === 'DRIVER') mappedType = 'driver';
      else if (typeStr === 'HELPER') mappedType = 'helper';
      else if (typeStr === 'SALES REP') mappedType = 'dsr';
      else if (typeStr === 'SALES EXECUTIVE') mappedType = 'se';
      else if (typeStr === 'DISTRIBUTOR BACK OFFICE') mappedType = 'back-office';
      else mappedType = typeStr.toLowerCase(); // fallback

      this.selectedStaffId = staff.id;
      this.selectedStaffType = mappedType;
      this.showStaffModal = true;
    },

    closeStaffModal() {
      this.showStaffModal = false;
      this.selectedStaffId = null;
      this.selectedStaffType = null;
      document.body.style.overflow = 'auto'; // Ensure scroll is restored
    },

    newStaff() {
      this.router.push({ path: "/new-staff" });
    },

    editStaff(staff) {
      let mappedType = '';
      const typeStr = String(staff.staff_type || staff.staf_type || '').toUpperCase().trim();
      if (typeStr === 'DRIVER') mappedType = 'driver';
      else if (typeStr === 'HELPER') mappedType = 'helper';
      else if (typeStr === 'SALES REP') mappedType = 'dsr';
      else if (typeStr === 'SALES EXECUTIVE') mappedType = 'se';
      else if (typeStr === 'DISTRIBUTOR BACK OFFICE') mappedType = 'back-office';
      else mappedType = typeStr.toLowerCase(); // fallback

      this.router.push({ path: `/edit-staff/${staff.id}`, query: { type: mappedType } });
    },

    isOfficeStaff(staff) {
      const typeStr = String(staff.staff_type || staff.staf_type || '').toUpperCase().trim();
      return typeStr === 'SALES REP' || typeStr === 'DISTRIBUTOR BACK OFFICE' || typeStr === 'SALES EXECUTIVE';
    },

    canUnblock(staff) {
      if (staff.is_blocked !== '1' && staff.is_blocked !== true && staff.is_blocked !== 1) return false;
      return this.isOfficeStaff(staff);
    },

    confirmUnblock(id) {
      this.unblockUserId = id;
      this.showUnblockDialog = true;
      document.body.style.overflow = "hidden";
    },

    async processUnblock() {
      try {
        this.unblocking = true;
        const response = await unblockUser(this.unblockUserId, this.token);
        if (response.data.status === "success") {
          useToast().success(response.data.message);
          this.showUnblockDialog = false;
          this.unblockUserId = null;
          await this.fetchStaff();
        } else {
          useToast().error(response.data.message);
        }
      } catch (e) {
        useToast().error("User unblock failed. Please try again.");
      } finally {
        this.unblocking = false;
        document.body.style.overflow = "auto";
      }
    },

    cancelUnblock() {
      this.showUnblockDialog = false;
      this.unblockUserId = null;
      document.body.style.overflow = "auto";
    }
  }
}
</script>

<style scoped lang="scss">
.card {
  padding: 10px;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
}

.filter-group {
  display: flex;
  gap: 15px;
  flex: 1;
}

.search-input {
  max-width: 300px;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  width: 100%;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.new-item-btn {
  margin: 0;
  padding: 10px 20px;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-eye:active {
  transform: scale(1.1);
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-edit:active {
  transform: scale(1.1);
}

.fa-unlock {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}
.fa-unlock:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-unlock:active {
  transform: scale(1.1);
}

.badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 5px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  min-width: 80px;
  max-width: 80px;
}

.badge-active {
  background-color: #28a745;
}

.badge-cancel {
  background-color: #dc3545;
}

.mobile-number {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

// added by isindu | 2025/ 12/ 02
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin: 20px 0;
  color: #007bff;
}

.no-data {
  color: #888;
  padding: 20px 0;
}

/* Dialog Overlay Styles */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog-box {
  background: white;
  border-radius: 8px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.dialog-header {
  padding: 20px 20px 0;
}

.dialog-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.dialog-body {
  padding: 20px;
}

.dialog-body p {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

.dialog-footer {
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>